/**
 * Per-tab active account — the source of truth for "which account is THIS tab
 * showing", stored in `sessionStorage`.
 */

import { createSignal } from "solid-js";
import { accountIdForAlias } from "@/lib/account-alias";

/** sessionStorage key holding THIS tab's active account userId. */
export const TAB_ACCOUNT_STORAGE_KEY = "tab_account";

/**
 * localStorage key remembering the last account activated on this DEVICE.
 * Only ever used to seed a FRESH tab (one with no sessionStorage entry yet):
 * it is a default, never an override of a tab's own choice.
 */
export const LAST_ACTIVE_ACCOUNT_KEY = "last_active_account";

/** Same-tab notification that this tab's active account changed. */
export const TAB_ACCOUNT_CHANGED_EVENT = "tab-account-changed";

/** Registry-changed event (mirrors the constant in `lib/accounts.ts`). */
export const ACCOUNTS_CHANGED_EVENT = "accounts-changed";

/** localStorage key of the shared accounts registry. */
export const ACCOUNTS_STORAGE_KEY = "accounts";

/**
 * Query parameter carrying the ONE-SHOT account handoff into a new tab.
 */
export const ACCOUNT_HANDOFF_PARAM = "as";

/** Module-level latch: the handoff is consumed exactly once per document. */
let handoffConsumed = false;

/**
 * Consumes a `?as=<alias>` handoff, if present, into this tab's pin and strips
 * the parameter from the URL via `history.replaceState`.
 */
export function consumeAccountHandoff(): void {
  if (handoffConsumed) {
    return;
  }
  handoffConsumed = true;
  let alias: string | null = null;
  let url: URL;
  try {
    url = new URL(globalThis.location.href);
    alias = url.searchParams.get(ACCOUNT_HANDOFF_PARAM);
  } catch {
    return;
  }
  if (alias === null) {
    return;
  }
  const userId = accountIdForAlias(alias);
  if (userId !== null) {
    try {
      globalThis.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, userId);
    } catch {
      // Storage unavailable
    }
    rememberLastActiveAccountId(userId);
  }
  try {
    url.searchParams.delete(ACCOUNT_HANDOFF_PARAM);
    globalThis.history.replaceState(globalThis.history.state, "", url.toString());
  } catch {
    // No History API
  }
  notifyTabAccountChanged();
}

/**
 * Synchronous read of THIS tab's active account userId, or `null` when the tab
 * has not resolved one yet.
 */
export function readTabAccountId(): string | null {
  consumeAccountHandoff();
  try {
    const raw = globalThis.sessionStorage.getItem(TAB_ACCOUNT_STORAGE_KEY);
    return raw === null || raw === "" ? null : raw;
  } catch {
    return null;
  }
}

/**
 * Pins THIS tab to `userId` and remembers it as the device-wide default for
 * future fresh tabs. Dispatches {@link TAB_ACCOUNT_CHANGED_EVENT}.
 */
export function writeTabAccountId(userId: string, options?: { silent?: boolean }): void {
  try {
    globalThis.sessionStorage.setItem(TAB_ACCOUNT_STORAGE_KEY, userId);
  } catch {
    // Storage unavailable
  }
  rememberLastActiveAccountId(userId);
  if (options?.silent !== true) {
    notifyTabAccountChanged();
  }
}

/** Clears this tab's pin (used when its account is removed). */
export function clearTabAccountId(): void {
  try {
    globalThis.sessionStorage.removeItem(TAB_ACCOUNT_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
  notifyTabAccountChanged();
}

/** Persists the device-wide "last activated account" default. */
export function rememberLastActiveAccountId(userId: string): void {
  try {
    globalThis.localStorage.setItem(LAST_ACTIVE_ACCOUNT_KEY, userId);
  } catch {
    // Best-effort only.
  }
}

/** Reads the device-wide "last activated account" default. */
export function readLastActiveAccountId(): string | null {
  try {
    const raw = globalThis.localStorage.getItem(LAST_ACTIVE_ACCOUNT_KEY);
    return raw === null || raw === "" ? null : raw;
  } catch {
    return null;
  }
}

/**
 * Emits the same-tab change event.
 */
export function notifyTabAccountChanged(): void {
  try {
    globalThis.dispatchEvent(new Event(TAB_ACCOUNT_CHANGED_EVENT));
  } catch {
    // No DOM
  }
}

// ─── Reactive accessor (app-lifetime singleton) ─────────────────────────────

let revisionAccessor: (() => number) | null = null;

function trackTabAccountChanges(): number {
  if (!revisionAccessor) {
    const [revision, setRevision] = createSignal(0, { equals: false });
    const bump = () => {
      setRevision((r) => r + 1);
    };
    if (typeof globalThis.addEventListener === "function") {
      globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, bump);
      globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, bump);
      globalThis.addEventListener("storage", (event) => {
        const { key } = event as StorageEvent;
        if (key === ACCOUNTS_STORAGE_KEY || key === null) {
          bump();
        }
      });
    }
    revisionAccessor = revision;
  }
  return revisionAccessor();
}

/**
 * REACTIVE accessor of this tab's active account userId.
 */
export function activeTabAccountId(): string | null {
  trackTabAccountChanges();
  return readTabAccountId();
}
